import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'

interface OtpInputProps {
  /** The digit strings (typically 6). */
  value: string[]
  /** Called with the next digit array on any change (type / paste / clear). */
  onChange: (next: string[]) => void
  /** Class applied to each digit box — styling differs per theme (card vs dark login);
   *  callers pass their own themed classes. */
  inputClassName?: string
  /**
   * Focus the first box whenever a fresh (all-empty) code is presented —
   * covers both entering the OTP step and the "Resend code" reset, where the
   * parent replaces the array with empty digits while the grid stays mounted.
   */
  autoFocusFirst?: boolean
}

/**
 * 6-box OTP entry grid with per-digit filtering, backspace navigation, and
 * paste-fill. Controlled — the parent owns the digits array.
 * Shared by PhoneNumberCard (profile settings) and PhoneLoginPanel (login).
 */
export function OtpInput({
  value,
  onChange,
  inputClassName = 'size-11 rounded-lg border border-border bg-background text-center text-lg font-semibold',
  autoFocusFirst = false,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    // Focus the first box whenever the grid is (or becomes) all-empty. This
    // intentionally covers both the initial entry into the OTP step and the
    // "Resend code" reset (parent replaces the array with empty digits), and
    // also re-focuses box 0 if the user clears every digit manually — a
    // deliberate, standard OTP convenience.
    if (autoFocusFirst && value.every((d) => d === '')) {
      refs.current[0]?.focus()
    }
  }, [autoFocusFirst, value])

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1)
    const next = [...value]
    next[index] = digit
    onChange(next)
    if (digit && index < value.length - 1) refs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && value[index] === '' && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent) => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, value.length).split('')
    if (digits.length === 0) return
    e.preventDefault()
    const next = Array(value.length).fill('') as string[]
    digits.forEach((d, i) => {
      next[i] = d
    })
    onChange(next)
    refs.current[Math.min(digits.length, value.length - 1)]?.focus()
  }

  return (
    <div className="flex justify-between gap-2" onPaste={handlePaste}>
      {value.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          aria-label={`Digit ${i + 1}`}
          className={inputClassName}
        />
      ))}
    </div>
  )
}
